/**
 * Fit Quiz — step navigation, answer collection and result rendering.
 *
 * Previously this ran as a ~14 KB inline <script> inside
 * sections/fitness-quiz.liquid. It carried no Liquid interpolation other than
 * the section id, so it has been moved here verbatim: the payload is now
 * cached by the browser and deferred instead of being re-sent (and executed
 * mid-parse) with every HTML response for a page containing the section.
 *
 * The sizing rules themselves live in assets/fit-quiz-logic.js
 * (window.RAPFitQuiz), which is loaded immediately before this file. Both are
 * deferred, and deferred scripts execute in document order, so RAPFitQuiz is
 * guaranteed to be present by the time this runs.
 *
 * Behaviour is unchanged. Instead of one script per section instance querying
 * its own id, this initialises every [data-fit-quiz] root it finds; the
 * original data-fq-init guard still makes initialisation idempotent.
 */
(function () {
  function initFitQuiz(root) {
    if (!root || root.dataset.fqInit === '1') return;
    root.dataset.fqInit = '1';

    var card = root.querySelector('[data-quiz-card]');
    var imageWrap = root.querySelector('[data-quiz-image]');
    var steps = Array.prototype.slice.call(card.querySelectorAll('.fyf-quiz-step'));
    var progressFill = card.querySelector('[data-progress-fill]');
    var progressLabel = card.querySelector('[data-progress-label]');
    var progressPercent = card.querySelector('[data-progress-percent]');
    var nav = card.querySelector('[data-quiz-nav]');
    var prevBtn = card.querySelector('[data-quiz-prev]');
    var nextBtn = card.querySelector('[data-quiz-next]');
    var skipBtn = card.querySelector('[data-quiz-skip]');
    var retakeBtn = card.querySelector('[data-quiz-retake]');
    var resultSizeEl = card.querySelector('[data-result-size]');
    var resultReasonEl = card.querySelector('[data-result-reason]');
    var resultRecheckEl = card.querySelector('[data-result-recheck]');
    var resultRecheckTextEl = card.querySelector('[data-result-recheck-text]');
    var productCards = Array.prototype.slice.call(card.querySelectorAll('[data-product-for]'));

    var TOTAL_STEPS = 5;
    var currentStep = 1;
    var answers = {};

    function setStep(stepKey) {
      steps.forEach(function(s) {
        s.classList.toggle('is-active', s.getAttribute('data-step') === String(stepKey));
      });

      var photos = imageWrap.querySelectorAll('.fyf-quiz-image-photo');
      photos.forEach(function(p) {
        var match = p.getAttribute('data-quiz-image-step') === String(stepKey);
        p.classList.toggle('is-active', match);
        if (match) { p.removeAttribute('hidden'); } else { p.setAttribute('hidden', ''); }
      });

      if (stepKey === 'result') {
        nav.style.display = 'none';
        progressLabel.textContent = 'Complete';
        progressPercent.textContent = '100%';
        progressFill.style.width = '100%';
      } else {
        nav.style.display = '';
        progressLabel.textContent = 'Question ' + stepKey + ' of ' + TOTAL_STEPS;
        var pct = Math.round((stepKey / TOTAL_STEPS) * 100);
        progressPercent.textContent = pct + '%';
        progressFill.style.width = pct + '%';
        prevBtn.disabled = stepKey === 1;
        nextBtn.innerHTML = (stepKey === TOTAL_STEPS)
          ? 'See My Size <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>'
          : 'Next <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>';
      }
    }

    function validateStep(stepNum) {
      if (stepNum === 1) return !!answers.size;
      if (stepNum === 2) return !!answers.shape;
      if (stepNum === 3) return !!answers['height-ft'] && answers['height-in'] !== undefined && answers['height-in'] !== '';
      if (stepNum === 4) return !!answers['fit-preference'];
      return true;
    }

    function readInputs(stepNum) {
      var stepEl = card.querySelector('.fyf-quiz-step[data-step="' + stepNum + '"]');
      if (!stepEl) return;
      stepEl.querySelectorAll('[data-quiz-field]').forEach(function(el) {
        if (el.tagName === 'BUTTON') return;
        var key = el.getAttribute('data-quiz-field');
        // Clear the answer when the field is empty. Without the delete, a value
        // the customer typed and then erased (e.g. a hip measurement) stayed in
        // `answers` and still drove the recommendation.
        if (el.value !== '') { answers[key] = el.value; }
        else { delete answers[key]; }
      });
    }

    card.querySelectorAll('.fyf-shape-card').forEach(function(btn) {
      btn.addEventListener('click', function() {
        card.querySelectorAll('.fyf-shape-card').forEach(function(b) {
          b.classList.remove('is-selected');
          b.setAttribute('aria-checked', 'false');
        });
        btn.classList.add('is-selected');
        btn.setAttribute('aria-checked', 'true');
        answers.shape = btn.getAttribute('data-value');
      });
    });

    card.querySelectorAll('.fyf-fit-card, .fyf-toggle-btn').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var group = btn.closest('.fyf-toggle-pair');
        group.querySelectorAll('.fyf-toggle-btn').forEach(function(b) {
          b.classList.remove('is-selected');
          b.setAttribute('aria-checked', 'false');
        });
        btn.classList.add('is-selected');
        btn.setAttribute('aria-checked', 'true');
        answers['fit-preference'] = btn.getAttribute('data-value');
      });
    });

    nextBtn.addEventListener('click', function() {
      readInputs(currentStep);
      if (!validateStep(currentStep)) {
        var stepEl = card.querySelector('.fyf-quiz-step[data-step="' + currentStep + '"]');
        stepEl.classList.remove('fq-shake');
        void stepEl.offsetWidth;
        stepEl.classList.add('fq-shake');
        return;
      }
      if (currentStep < TOTAL_STEPS) {
        currentStep++;
        setStep(currentStep);
      } else {
        readInputs(5);
        showResult();
      }
    });

    prevBtn.addEventListener('click', function() {
      if (currentStep > 1) {
        currentStep--;
        setStep(currentStep);
      }
    });

    if (skipBtn) {
      skipBtn.addEventListener('click', function() {
        // "Skip & See My Size" must really skip: drop anything already typed into
        // the optional measurement fields so they can't sway the recommendation.
        var stepEl = card.querySelector('.fyf-quiz-step[data-step="5"]');
        if (stepEl) {
          stepEl.querySelectorAll('input[type="number"]').forEach(function(el){ el.value = ''; });
        }
        readInputs(5);
        showResult();
      });
    }

    if (retakeBtn) {
      retakeBtn.addEventListener('click', function() {
        answers = {};
        currentStep = 1;
        card.querySelectorAll('select, input[type="number"]').forEach(function(el){ el.value = ''; });
        card.querySelectorAll('.is-selected').forEach(function(el){
          el.classList.remove('is-selected');
          el.setAttribute('aria-checked', 'false');
        });
        setStep(1);
      });
    }

    // Sizing logic lives in assets/fit-quiz-logic.js (window.RAPFitQuiz) so the
    // exact same rules power both this quiz and the verification harness
    // (assets/fit-quiz-test.html). Do not duplicate the rules here.
    function computeResult() {
      if (!global_RAPFitQuiz()) {
        // Logic file failed to load — fail safe instead of throwing.
        return { size: NaN, fit: 'Straight', length: 'Regular', shape: answers.shape || 'straight', fitPref: answers['fit-preference'], usedMeasurements: false };
      }
      return window.RAPFitQuiz.computeResult(answers);
    }

    function global_RAPFitQuiz() {
      return typeof window !== 'undefined' && window.RAPFitQuiz && typeof window.RAPFitQuiz.computeResult === 'function';
    }

    function showResult() {
      var r = computeResult();
      var sizeLabel = 'Size ' + (isNaN(r.size) ? '—' : r.size) + ' ' + r.fit + ', ' + r.length + ' Length';
      resultSizeEl.textContent = sizeLabel;

      var reason;
      if (r.usedMeasurements) {
        reason = 'Based on your waist and hip measurements';
        if (r.fitPref) {
          reason += ' and your preference for a ' + (r.fitPref === 'fitted' ? 'more fitted' : 'looser') + ' fit';
        }
        if (r.shape === 'very-curvy') reason += ', and Very Curvy shape (which runs a bit larger)';
        reason += ', the ' + r.fit + ' fit in a size ' + r.size + ' with a ' + r.length.toLowerCase() + ' inseam will give you the best fit through the hip, thigh, and leg.';
      } else {
        var bits = [];
        if (!isNaN(r.size)) bits.push('current size');
        bits.push('shape');
        if (answers['height-ft']) bits.push('height');
        reason = 'Based on your ' + bits.join(', ');
        if (r.shape === 'very-curvy') reason += ' (Very Curvy runs a bit larger, so we sized down)';
        reason += ', the ' + r.fit + ' fit in a size ' + r.size + ' with a ' + r.length.toLowerCase() + ' inseam will give you the best fit through the hip, thigh, and leg.';
      }
      resultReasonEl.textContent = reason;

      // Show a gentle double-check notice when the inputs disagree (e.g. the
      // measurements point to a very different size than the stated one, or a
      // measurement falls outside our size chart). We still show a size — based
      // on the stated size, the safer signal — but ask them to confirm.
      if (resultRecheckEl) {
        if (r.recheck) {
          var msg = (r.recheck.reason === 'measurement-out-of-range')
            ? 'One of your measurements is outside our size range, so it may have been entered incorrectly. We’ve used your usual size for now — please double-check your waist and hip.'
            : 'Your measurements point to a very different size than the one you usually wear. We’ve gone with your usual size to be safe — please double-check your waist and hip measurements.';
          if (resultRecheckTextEl) { resultRecheckTextEl.textContent = msg; }
          else { resultRecheckEl.textContent = msg; }
          resultRecheckEl.removeAttribute('hidden');
        } else {
          resultRecheckEl.setAttribute('hidden', '');
        }
      }

      var shapeKey = (r.shape === 'very-curvy') ? 'curvy' : 'straight';
      var hasCurvyCard = productCards.some(function(c) { return c.getAttribute('data-product-for') === 'curvy'; });
      var hasStraightCard = productCards.some(function(c) { return c.getAttribute('data-product-for') === 'straight'; });
      if (shapeKey === 'curvy' && !hasCurvyCard) shapeKey = 'straight';
      if (shapeKey === 'straight' && !hasStraightCard && hasCurvyCard) shapeKey = 'curvy';

      productCards.forEach(function(c) {
        var match = c.getAttribute('data-product-for') === shapeKey;
        if (match) {
          c.removeAttribute('hidden');
          var sizeEl = c.querySelector('[data-result-product-size]');
          if (sizeEl) sizeEl.textContent = (isNaN(r.size) ? '—' : r.size) + ' ' + r.fit + ' · ' + r.length;
          applyVariantForCard(c, r.size, r.length);
        } else {
          c.setAttribute('hidden', '');
        }
      });

      setStep('result');
    }

    function findVariant(card, sizeStr, lengthStr) {
      var vEl = card.querySelector('[data-fq-variants]');
      if (!vEl) return null;
      var variants;
      try {
        variants = JSON.parse(vEl.textContent);
      } catch (e) { return null; }
      if (!variants || !variants.length) return null;

      var sizeNorm = String(sizeStr == null ? '' : sizeStr).trim();
      var lengthNorm = lengthStr ? String(lengthStr).toLowerCase().trim() : '';

      // Liquid pre-computed the per-variant size and length values for us.
      var sized = variants.filter(function(v) {
        return String(v.size == null ? '' : v.size).trim() === sizeNorm;
      });
      if (!sized.length) return null;

      if (lengthNorm) {
        var lengthMatched = sized.filter(function(v) {
          return v.length != null && String(v.length).toLowerCase().indexOf(lengthNorm) !== -1;
        });
        if (lengthMatched.length) sized = lengthMatched;
      }

      for (var i = 0; i < sized.length; i++) {
        if (sized[i].available) return sized[i];
      }
      return sized[0];
    }

    function applyVariantForCard(card, sizeStr, lengthStr) {
      var addBtn = card.querySelector('[data-result-add-to-cart]');
      if (!addBtn) return;
      if (sizeStr == null || sizeStr === '') return;

      var variant = findVariant(card, sizeStr, lengthStr);
      if (!variant) {
        var label = addBtn.querySelector('[data-add-label]');
        if (label) label.textContent = 'Size unavailable';
        addBtn.disabled = true;
        return;
      }
      addBtn.setAttribute('data-variant-id', variant.id);
      addBtn.disabled = !variant.available;
      var label2 = addBtn.querySelector('[data-add-label]');
      if (label2) label2.textContent = variant.available ? 'Add to Cart' : 'Sold Out';

      var priceEl = card.querySelector('.fyf-result-product-price');
      if (priceEl && typeof variant.price === 'number') {
        priceEl.textContent = '$' + (variant.price / 100).toFixed(2);
      }
    }

    card.querySelectorAll('[data-result-add-to-cart]').forEach(function(addBtn) {
      addBtn.addEventListener('click', function() {
        var variantId = addBtn.getAttribute('data-variant-id');
        if (!variantId) return;
        var label = addBtn.querySelector('[data-add-label]');
        var originalLabel = label ? label.textContent : '';
        addBtn.disabled = true;
        if (label) label.textContent = 'Adding...';

        fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
        })
          .then(function(res) {
            if (!res.ok) throw new Error('Add to cart failed');
            return res.json();
          })
          .then(function() {
            if (label) label.textContent = 'Added!';
            var cartUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
              ? window.Shopify.routes.root + 'cart'
              : '/cart';
            window.location.href = cartUrl;
          })
          .catch(function() {
            if (label) label.textContent = 'Try again';
            setTimeout(function() {
              if (label) label.textContent = originalLabel || 'Add to Cart';
              addBtn.disabled = false;
            }, 1500);
          });
      });
    });

    var modal = root.querySelector('[data-measure-modal]');
    var openLink = root.querySelector('[data-open-measure-modal]');
    if (modal && openLink) {
      var openModal = function(e) {
        if (e) e.preventDefault();
        modal.removeAttribute('hidden');
        document.body.style.overflow = 'hidden';
      };
      var closeModal = function() {
        modal.setAttribute('hidden', '');
        document.body.style.overflow = '';
      };
      openLink.addEventListener('click', openModal);
      modal.querySelectorAll('[data-modal-close]').forEach(function(el) {
        el.addEventListener('click', closeModal);
      });
      document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && !modal.hasAttribute('hidden')) closeModal();
      });
    }
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-fit-quiz]');
    for (var i = 0; i < roots.length; i++) {
      initFitQuiz(roots[i]);
    }
    // A section re-rendered by the theme editor arrives as the root itself.
    if (scope && scope.matches && scope.matches('[data-fit-quiz]')) {
      initFitQuiz(scope);
    }
  }

  initAll(document);

  // The theme editor replaces section markup without re-running external
  // scripts, so re-initialise the fresh DOM after every section render.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
